import { Participant } from 'twilio-video';
import { MainParticipant } from '~twilio/components/main-participant';
import { Participant as ParticipantTrack } from '~twilio/components/participant';
import TimerButton from '~twilio/components/timer-button';
import { ToggleAudioButton } from '~twilio/components/toggle-audio-button';
import { ToggleScreenShareButton } from '~twilio/components/toggle-screen-share-button';
import { ToggleVideoButton } from '~twilio/components/toggle-video-button';
import useSelectedParticipant from '~twilio/components/video-provider/use-selected-participant';
import useDominantSpeaker from '~twilio/hooks/use-dominant-speaker';
import useMainParticipant from '~twilio/hooks/use-main-participant';
import useParticipantsContext from '~twilio/hooks/use-participants-context';
import useVideoContext from '~twilio/hooks/use-video-context';
import { cn } from '~utils/tailwind';
import MobileAccess from './mobile-access';

const MobileSignature = '#mobile';

const ParticipantsRoom = () => {
  const { room } = useVideoContext();
  const dominantSpeaker = useDominantSpeaker(true);
  const { galleryViewParticipants } = useParticipantsContext();
  const [selectedParticipant, setSelectedParticipant] =
    useSelectedParticipant();

  const localParticipant = room?.localParticipant as Participant;
  const counterParticipant = galleryViewParticipants.find(
    (participant) => !participant.identity.includes(MobileSignature)
  );
  const mobileParticipant = galleryViewParticipants.find((participant) =>
    participant.identity.includes(MobileSignature)
  );
  const mainParticipant = useMainParticipant();

  return (
    <div className='relative flex h-full w-full flex-col items-center justify-center'>
      {!mobileParticipant && (
        <div className='absolute bottom-0 left-0 p-8 opacity-40 hover:opacity-100' />
      )}
      <div className='absolute right-0 top-0 z-20 flex h-full flex-col gap-4 p-8 align-baseline'>
        {/* 수의사가 접속하지 않았으면 우측 썸네일 화면들 전부 안보임  */}
        {!counterParticipant ? null : (
          <>
            {/* 메인 화면이 보호자가 아닐 때 우측상단에 보호자 화면  */}
            {mainParticipant?.identity !== localParticipant?.identity && (
              <div className='flex h-[240px] w-[240px] items-center justify-center rounded-[18px] bg-black lg:h-[320px] lg:w-[320px]'>
                <ParticipantTrack
                  participant={localParticipant}
                  isLocalParticipant
                  isDominantSpeaker={dominantSpeaker === localParticipant}
                  isSelected={selectedParticipant === localParticipant}
                  enableScreenShare
                  onClick={() => {
                    setSelectedParticipant(localParticipant);
                  }}
                />
              </div>
            )}

            {/* 메인 화면이 수의사가 아닐 때 우측상단에 수의사 화면  */}
            {mainParticipant?.identity !== counterParticipant?.identity && (
              <div className='flex h-[240px] w-[240px] items-center justify-center rounded-[18px] bg-black lg:h-[320px] lg:w-[320px]'>
                {counterParticipant ? (
                  <ParticipantTrack
                    participant={counterParticipant}
                    isDominantSpeaker={dominantSpeaker === counterParticipant}
                    isSelected={selectedParticipant === counterParticipant}
                    onClick={() => {
                      setSelectedParticipant(counterParticipant);
                    }}
                  />
                ) : (
                  <p className='text-white text-body-1'>
                    Waiting for the vet...
                  </p>
                )}
              </div>
            )}

            {/* 메인 화면이 모바일이 아닐 때 우측상단에 모바일 화면  */}
            {mainParticipant?.identity !== mobileParticipant?.identity && (
              <div className='flex h-[240px] w-[240px] items-center justify-center rounded-[18px] bg-black lg:h-[320px] lg:w-[320px]'>
                {mobileParticipant ? (
                  <ParticipantTrack
                    participant={mobileParticipant}
                    isDominantSpeaker={dominantSpeaker === mobileParticipant}
                    isSelected={selectedParticipant === mobileParticipant}
                    onClick={() => {
                      setSelectedParticipant(mobileParticipant);
                    }}
                  />
                ) : (
                  <div className='flex h-[240px] w-[240px] flex-col items-center justify-center gap-3 rounded-[18px] bg-white p-2 lg:h-[320px] lg:w-[320px]'>
                    <div className='px-5 lg:p-0'>
                      <MobileAccess />
                    </div>
                    <p className='text-center text-gray-900 text-label-2 lg:text-title-3'>
                      Scan the code to connect your phone
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div
        className={cn(
          'flex h-full w-full flex-row items-center justify-center ',
          counterParticipant
            ? 'bg-black'
            : 'bg-gradient-to-t from-[#111419] to-[#12336B]'
        )}
      >
        {counterParticipant ? (
          <MainParticipant />
        ) : (
          <p className=' text-white text-headline-1'>Please wait...</p>
        )}
      </div>
      <div className='absolute bottom-[5%] left-0 z-30 flex w-full flex-row justify-center'>
        <div className='flex flex-row items-center justify-center rounded-2xl bg-black bg-opacity-80 p-3'>
          <div className='flex flex-row items-center justify-center gap-[48px]'>
            <div className='relative flex flex-row gap-[16px] overflow-hidden'>
              <ToggleAudioButton />
              <ToggleVideoButton />
              <ToggleScreenShareButton />
            </div>
            <TimerButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantsRoom;
